/**
 * ReportSidebarActions
 * Renders report-level actions (PDF download, investor visibility toggle)
 * at the bottom of the DimensionNav sidebar. Only renders when inside a ReportProvider.
 *
 * STA-228: Provides a persistent home for PDF generation/download and
 * investor sharing controls that were displaced when the overview page was removed.
 *
 * STA-272: PDFs auto-generate on transition to REVIEWING_REPORT (backend).
 * The manual "Generate" button has been removed — users see a spinner while
 * the report is being produced, or an inline error if generation fails.
 */

import {
  useOptionalReportContext,
  useReportContext,
} from "@/contexts/ReportContext";
import { useReports } from "@/hooks/useReports";
import { useAssessment } from "@/hooks/useAssessment";
import { useSetInvestorVisibility } from "@/hooks/useInvestorAccess";
import { useShareReport } from "@/hooks/useShareReport";
import { InvestorProgressVisibilityNotice } from "@/components/investor/InvestorProgressVisibilityNotice";
import { useTenant } from "@/contexts/TenantContext";
import { useToast } from "@/hooks/use-toast";
import { AssessmentWorkflowState } from "@app/types";
import {
  AlertCircle,
  FileText,
  Loader2,
  Eye,
  EyeOff,
  Share2,
} from "lucide-react";

/**
 * Compact report actions for the sidebar: PDF download and investor visibility.
 * Returns null when rendered outside a ReportProvider (graceful degradation).
 *
 * @returns The sidebar actions section, or null if no report context is available
 */
export function ReportSidebarActions(): JSX.Element | null {
  const reportCtx = useOptionalReportContext();

  if (!reportCtx) return null;

  return <ReportSidebarActionsInner />;
}

/**
 * Inner component that safely uses useReportContext (guaranteed by parent guard).
 * Split out so hooks are called unconditionally within the component.
 *
 * Layout: two clear sections separated by dividers:
 * 1. Reports Section - generate button, download links
 * 2. Sharing Section - share report gate, investor visibility toggle
 */
function ReportSidebarActionsInner(): JSX.Element {
  const { assessmentId, isInvestorView, hookOptions } = useReportContext();

  // Don't force `latest: true` — the server drops failed exports from the
  // latest-only response, which would make our failed-state banner unreachable.
  // `investmentMemorandum` / `technicalAssessment` are already derived as the
  // most-recent *completed* export of each type inside the hook.
  const {
    reports,
    isReportInProgress,
    downloadReport,
    investmentMemorandum,
    technicalAssessment,
  } = useReports(assessmentId, hookOptions);

  const { toast } = useToast();

  const hasFailedReport = reports.some((r) => r.status === "failed");
  const hasCompletedReport = !!investmentMemorandum || !!technicalAssessment;

  const handleDownload = async (reportId: string) => {
    try {
      await downloadReport(reportId);
    } catch (err) {
      console.error("[ReportSidebarActions] Failed to download report:", err);
      toast({
        title: "Failed to download report",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  const renderReportActions = () => {
    // Nothing to show — no completed downloads, nothing in progress, nothing failed.
    // Hide the whole Reports section until the auto-gen kicks off or something fails.
    if (!hasCompletedReport && !isReportInProgress && !hasFailedReport) {
      return null;
    }

    // Failed + no completed exports ever → surface only the alert.
    // Failed + at least one completed → surface BOTH (alert above downloads).
    // Generating + no completed → spinner only.
    // Generating + at least one completed → spinner above downloads.
    return (
      <div className="space-y-1">
        {hasFailedReport && (
          <div
            data-testid="report-failed-state"
            role="alert"
            className="flex items-start gap-2 px-3 py-2 text-sm rounded-md bg-destructive/10 text-destructive"
          >
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              Report generation failed. The StackVerify team has been notified.
            </span>
          </div>
        )}
        {isReportInProgress && (
          <div
            data-testid="report-generating-spinner"
            role="status"
            aria-live="polite"
            aria-busy="true"
            className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Generating reports...</span>
          </div>
        )}
        {investmentMemorandum && (
          <button
            type="button"
            data-testid="report-download-im-btn"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md text-primary hover:bg-muted transition-colors"
            onClick={() => handleDownload(investmentMemorandum.id)}
          >
            <FileText className="h-4 w-4" />
            Investment Memorandum
          </button>
        )}
        {technicalAssessment && (
          <button
            type="button"
            data-testid="report-download-ta-btn"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md text-primary hover:bg-muted transition-colors"
            onClick={() => handleDownload(technicalAssessment.id)}
          >
            <FileText className="h-4 w-4" />
            Technical Assessment
          </button>
        )}
      </div>
    );
  };

  const reportActions = renderReportActions();

  return (
    <>
      {/* Reports Section */}
      {reportActions && (
        <>
          <div className="h-px bg-border my-3" />
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-3 mb-2">
            Reports
          </p>
          <div className="space-y-1.5 px-3">{reportActions}</div>
        </>
      )}

      {/* Sharing Section — only for non-investor views */}
      {!isInvestorView && (
        <>
          <div className="h-px bg-border my-3" />
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-3 mb-2">
            Sharing
          </p>
          <div className="space-y-1.5 px-3">
            <InvestorVisibilityButton assessmentId={assessmentId} />
            <InvestorProgressVisibilityNotice />
          </div>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Investor Visibility Button
// ---------------------------------------------------------------------------

interface InvestorVisibilityButtonProps {
  assessmentId: string;
}

/**
 * Compact toggle for sharing an assessment with linked investor funds.
 *
 * When the assessment is in REVIEWING_REPORT state, renders a "Share Report"
 * button that transitions to SHARED (required before investor visibility can be
 * toggled). Once frozen (SHARED or FINALIZED), renders the visibility toggle.
 *
 * Only renders for admin/owner roles.
 *
 * @param assessmentId - The assessment to toggle visibility for
 */
function InvestorVisibilityButton({
  assessmentId,
}: InvestorVisibilityButtonProps): JSX.Element | null {
  const { currentTenant } = useTenant();
  const { assessment } = useAssessment(assessmentId);
  const setVisibility = useSetInvestorVisibility();
  const shareReport = useShareReport(assessmentId);
  const { toast } = useToast();

  const isAdminOrOwner =
    currentTenant?.memberRole === "owner" ||
    currentTenant?.memberRole === "admin";

  if (!isAdminOrOwner || !assessment) return null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const workflowState = (assessment as any).workflowState as string;

  // Assessment is ready to be shared but not yet frozen — show the one-way Share button.
  if (workflowState === AssessmentWorkflowState.REVIEWING_REPORT) {
    const handleShareReport = async () => {
      try {
        await shareReport.mutateAsync();
        toast({
          title: "Report shared with investors",
          description:
            "The report is now frozen and visible to linked investor funds.",
        });
      } catch (err) {
        console.error("[ReportSidebarActions] Failed to share report:", err);
        toast({
          title: "Failed to share report",
          description: err instanceof Error ? err.message : "Please try again.",
          variant: "destructive",
        });
      }
    };

    return (
      <button
        type="button"
        data-testid="report-share-btn"
        onClick={handleShareReport}
        disabled={shareReport.isPending}
        className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium text-foreground hover:bg-accent transition-colors disabled:opacity-50"
      >
        {shareReport.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Share2 className="h-4 w-4" />
        )}
        Share with Investors
      </button>
    );
  }

  // Report is frozen — show the investor visibility toggle.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isShared = (assessment as any).sharedWithInvestors === true;

  const handleToggle = async () => {
    try {
      await setVisibility.mutateAsync({
        assessmentId,
        sharedWithInvestors: !isShared,
      });
      toast({
        title: !isShared ? "Shared with investors" : "Hidden from investors",
        description: !isShared
          ? "Linked investor funds can now view this assessment."
          : "This assessment is no longer visible to investor funds.",
      });
    } catch (err) {
      console.error("[ReportSidebarActions] Failed to update visibility:", err);
      toast({
        title: "Failed to update visibility",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <button
      type="button"
      data-testid="report-visibility-toggle"
      onClick={handleToggle}
      disabled={setVisibility.isPending}
      className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
        isShared
          ? "text-green-700 hover:bg-green-50"
          : "text-foreground hover:bg-accent"
      }`}
    >
      {isShared ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
      {isShared ? "Shared with Investors" : "Hidden from Investors"}
    </button>
  );
}
